import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import SliceRevealLogo from '../hero/SliceRevealLogo';
import MetaCorner from '../hero/MetaCorner';
import EditorialLink from '../hero/EditorialLink';
import LiveTimestamp from '../hero/LiveTimestamp';
import { useLang } from '../../context/LangContext';
import { fr } from '../../i18n/fr';
import { en } from '../../i18n/en';
import { de } from '../../i18n/de';

const T = { fr, en, de };
const EASE = [0.16, 1, 0.3, 1];

export default function Hero({ introDone = true }) {
  const [parallaxOn, setParallaxOn] = useState(false);
  const sectionRef = useRef(null);
  const { lang } = useLang();
  const t = T[lang]?.hero || T.fr.hero;

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start start', 'end start'] });
  const coverY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const coverScale = useTransform(scrollYProgress, [0, 1], [1.06, 1.14]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  useEffect(() => {
    if (!introDone) return;
    const id = setTimeout(() => setParallaxOn(true), 2400);
    return () => clearTimeout(id);
  }, [introDone]);

  const scrollToRecord = useCallback(() => {
    document.getElementById('record')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <>
      <section
        ref={sectionRef}
        style={{
          position: 'relative',
          height: '100vh',
          overflow: 'hidden',
          background: 'var(--bg)',
        }}
      >
        {/* Cover photo (fade-in from black, parallax on scroll) */}
        <motion.div
          style={{
            position: 'absolute',
            inset: '-10% 0',
            y: coverY,
            scale: coverScale,
            zIndex: 0,
          }}
          initial={{ opacity: 0, filter: 'blur(20px)' }}
          animate={introDone ? { opacity: 1, filter: 'blur(0px)' } : { opacity: 0, filter: 'blur(20px)' }}
          transition={{ delay: 0.4, duration: 2.0, ease: EASE }}
        >
          <img
            src="/TSANGA cover (full) .jpg"
            alt=""
            aria-hidden="true"
            loading="eager"
            fetchPriority="high"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              filter: 'contrast(1.06) saturate(0.75)',
              display: 'block',
            }}
          />
        </motion.div>

        {/* Vignette */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            background:
              'linear-gradient(180deg,rgba(4,4,10,0.5) 0%,rgba(4,4,10,0.1) 30%,rgba(4,4,10,0.2) 55%,rgba(4,4,10,0.9) 84%,rgba(4,4,10,1) 100%)',
          }}
        />
        <div className="noise" aria-hidden="true" style={{ zIndex: 2 }} />

        {/* Meta corners */}
        <MetaCorner
          position="TL"
          animate={introDone}
          lines={['A PIECE OF SKY', 'CSC RECORDS · 2025']}
        />
        <MetaCorner
          position="TR"
          animate={introDone}
          hideOnMobile
          lines={['50.85°N 4.35°E', 'BRUXELLES']}
        />
        <MetaCorner
          position="BL"
          animate={introDone}
          hideOnMobile
          lines={['EP 001', 'ARCHITECTE DU VIDE SONORE']}
        />
        <MetaCorner position="BR" animate={introDone}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
            <EditorialLink
              number="01"
              icon={<span style={{ fontSize: 12, lineHeight: 1 }}>↓</span>}
              bouncyIcon
              onClick={scrollToRecord}
              aria-label={t.listen}
            >
              {t.listen}
            </EditorialLink>
            <LiveTimestamp style={{ marginTop: 8 }} />
          </div>
        </MetaCorner>

        {/* Center column: logo + tagline */}
        <motion.div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: contentOpacity,
            pointerEvents: 'none',
          }}
        >
          <SliceRevealLogo animate={introDone} parallaxEnabled={parallaxOn} />

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={introDone ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
            transition={{ delay: 1.0, duration: 1.0, ease: EASE }}
            style={{
              marginTop: 'clamp(24px, 3vw, 40px)',
              fontFamily: 'var(--mono)',
              fontSize: 'clamp(9px, 1vw, 11px)',
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.45)',
              textAlign: 'center',
            }}
          >
            {t.tagline}
          </motion.p>
        </motion.div>
      </section>
    </>
  );
}
